import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
} from "@/components/ui/drawer";
import { EmailIntegrationCard } from "@/features/settings/forwarding-integrations/email-integration-card";
import { UploadBox } from "@/features/transactions/company/invoice-matching-panel/upload-box";
import { InvoiceType } from "@/lib/constants/invoiceStatus";
import { retrieveObjectsFromLocal } from "@/persistent-storage/localStorage";
import { useTenant } from "@/providers/session";
import { useGetAllEmailForwardings } from "@/queries/email-forwardings/get-all";
import { X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  invoiceType?: InvoiceType;
};

export function UploadInvoicesDrawer({
  isOpen,
  setIsOpen,
  invoiceType = InvoiceType.PAYABLE,
}: Props) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const { emailForwardings, loading, error } = useGetAllEmailForwardings();

  useCloseSheetOnUpload();

  return (
    <Drawer
      direction="right"
      open={isOpen}
      data-vaul-no-drag
      closeThreshold={1}
      dismissible={false}
      onOpenChange={setIsOpen}
    >
      <DrawerPortal data-vaul-no-drag>
        <DrawerOverlay
          className="fixed inset-0 bg-black/20"
          data-vaul-no-drag
        />
        <DrawerContent
          onOpenAutoFocus={(event: any) => {
            event?.preventDefault;
          }}
          data-vaul-no-drag
          onCloseAutoFocus={(event: any) => {
            event?.preventDefault;
          }}
          onInteractOutside={(event: any) => {
            setIsOpen(false);
          }}
          className="bg-white z-50 flex flex-col h-full w-[700px] mt-24 fixed bottom-0 right-0 max-h-screen min-h-screen overflow-y-auto overflow-x-hidden [&>div:first-child]:hidden"
        >
          <button
            aria-label={t("close")}
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full hover:bg-gray-100 transition-colors"
            type="button"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
          <div data-vaul-no-drag className="p-5 bg-white flex-1 h-full">
            <div className="p-4">
              <DrawerHeader>
                <DrawerTitle>{t("payables.upload_sheet_title")}</DrawerTitle>
              </DrawerHeader>
              <div className="my-4">
                <UploadBox tenantId={tenantId} invoiceType={invoiceType} />
              </div>
              {!loading &&
                !error &&
                emailForwardings.map((emailForwarding) => (
                  <EmailIntegrationCard
                    key={emailForwarding.emailForwardingId}
                    emailForwardingId={emailForwarding.emailForwardingId}
                    isActive={emailForwarding.isActive}
                    email={emailForwarding.forwardingAddress}
                    hideToggle
                    onDeleteEmailForwarding={async () => {}}
                    onUpdateEmailForwarding={async () => {}}
                  />
                ))}
            </div>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );

  function useCloseSheetOnUpload() {
    useEffect(() => {
      function handleStorageChange() {
        const notificationsItems = retrieveObjectsFromLocal(
          `invoiceUploadStatus-${tenantId}`,
        );
        if (notificationsItems.length > 0) {
          setIsOpen(false);
        }
      }

      document.addEventListener(
        "invoiceChangedLocalStorage",
        handleStorageChange,
      );
      document.addEventListener("tenantChanged", handleStorageChange);

      return () => {
        document.removeEventListener(
          "invoiceChangedLocalStorage",
          handleStorageChange,
        );
        document.removeEventListener("tenantChanged", handleStorageChange);
      };
    }, [tenantId]);
  }
}
